import { getToken, onMessage, deleteToken } from 'firebase/messaging';
import { getFirebaseMessaging } from '../config/firebase';
import api from './api';
import pushNotificationService from './pushNotificationService';
import { VAPID_PUBLIC_KEY } from '../config/push';

// Firebase Web Push VAPID key — get from:
// Firebase Console → Project Settings → Cloud Messaging → Web Push certificates
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

const FCM_TOKEN_KEY = 'fcm_token';

// ── Platform helpers ────────────────────────────────────────────────────────
// iOS Safari: Firebase JS SDK does NOT support push on iOS at all.
// iOS 16.4+ PWAs installed to home screen use standard VAPID Web Push instead.
const _isIOS     = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const _isAndroid = () => /Android/i.test(navigator.userAgent);

const fcmService = {
  _swReg: null,        // stored during init() so showForegroundNotification uses the right registration
  _initPromise: null,  // deduplicates concurrent init() calls

  /**
   * Register the firebase-messaging-sw.js service worker, request notification
   * permission, get an FCM token, and save it to the backend.
   * Idempotent — safe to call from multiple components; only runs once per session.
   */
  async init() {
    // Return existing promise if already initialising or done
    if (this._initPromise) return this._initPromise;

    this._initPromise = this._doInit();
    return this._initPromise;
  },

  async _doInit() {
    if (!VAPID_KEY) {
      console.warn('[FCM] REACT_APP_FIREBASE_VAPID_KEY not set — skipping FCM init.');
      return null;
    }

    if (!('serviceWorker' in navigator) || !('Notification' in window)) {
      console.warn('[FCM] Push notifications not supported in this browser.');
      return null;
    }

    // iOS Safari — Firebase JS SDK does NOT support push on iOS.
    // Route to VAPID Web Push (works on iOS 16.4+ when the app is installed to home screen).
    if (_isIOS()) {
      return this._doInitIOS();
    }

    // Request notification permission
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.info('[FCM] Notification permission not granted.');
      return null;
    }

    // Get Firebase Messaging instance (null on unsupported browsers)
    const messaging = await getFirebaseMessaging();
    if (!messaging) return null;

    try {
      // Register (or reuse) the FCM service worker
      const swReg = await navigator.serviceWorker.register(
        '/firebase-messaging-sw.js',
        { scope: '/firebase-cloud-messaging-push-scope' }
      );

      // PushManager.subscribe() requires an *active* SW — wait if still installing/waiting
      if (!swReg.active) {
        await new Promise((resolve) => {
          const sw = swReg.installing || swReg.waiting;
          if (!sw) return resolve(); // already active or nothing to wait for
          sw.addEventListener('statechange', function handler() {
            if (sw.state === 'activated' || swReg.active) {
              sw.removeEventListener('statechange', handler);
              resolve();
            }
          });
        });
      }

      this._swReg = swReg; // store for use in showForegroundNotification

      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: swReg,
      });

      if (!token) return null;

      // Always save on each login session — the backend replaces old tokens,
      // so this keeps the DB in sync even when the browser rotates the FCM token.
      const cached = localStorage.getItem(FCM_TOKEN_KEY);
      // Tag Android Chrome separately so the backend can distinguish platforms.
      const deviceType = _isAndroid() ? 'android' : 'web';
      await api.post('/fcm/token', { token, device_type: deviceType });
      localStorage.setItem(FCM_TOKEN_KEY, token);
      if (token !== cached) {
        console.info('[FCM] New token registered.');
      } else {
        console.info('[FCM] Token refreshed.');
      }

      return token;
    } catch (error) {
      console.error('[FCM] Token registration failed:', error);
      return null;
    }
  },

  /**
   * iOS Safari path — uses standard VAPID Web Push instead of Firebase SDK.
   * Requires the app to be installed to home screen (iOS 16.4+).
   */
  async _doInitIOS() {
    if (!('PushManager' in window)) {
      console.info('[FCM] iOS: Push API not available. Install the app to your home screen via Safari → Share → Add to Home Screen.');
      return null;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.info('[FCM] iOS: Notification permission not granted.');
        return null;
      }

      if (!VAPID_PUBLIC_KEY) {
        console.warn('[FCM] iOS: REACT_APP_VAPID_PUBLIC_KEY not set — skipping VAPID subscription.');
        return null;
      }

      await pushNotificationService.subscribe(VAPID_PUBLIC_KEY);
      console.info('[FCM] iOS: VAPID push subscription registered (service-worker.js).');
      return 'ios-vapid';
    } catch (e) {
      // Common failure: not installed to home screen, or iOS < 16.4
      console.warn('[FCM] iOS: VAPID subscription failed:', e.message);
      return null;
    }
  },

  /**
   * Listen for FCM messages while the app is in the foreground.
   * Returns an unsubscribe function.
   */
  async onForegroundMessage(callback) {
    const messaging = await getFirebaseMessaging();
    if (!messaging) return () => {};
    return onMessage(messaging, callback);
  },

  /**
   * Show an OS tray notification when the app is in the foreground.
   * Uses the stored _swReg from init() — avoids navigator.serviceWorker.ready
   * returning the wrong (main) service worker registration.
   */
  showForegroundNotification(payload) {
    if (Notification.permission !== 'granted') return;
    if (!this._swReg) {
      console.warn('[FCM] showForegroundNotification called before init() completed.');
      return;
    }

    // notification field is present on foreground messages
    const notification = payload.notification || {};
    const data         = payload.data         || {};

    const title   = notification.title || data.title || 'Ifesteimo Transport';
    const options = {
      body:               notification.body || data.body || 'You have a new notification.',
      icon:               '/logo192.png',
      badge:              '/logo192.png',
      vibrate:            [200, 100, 200],
      tag:                data.type || 'general',
      requireInteraction: false,
      data: {
        url:     data.url     || '/',
        type:    data.type    || '',
        ride_id: data.ride_id || '',
        role:    data.role    || '',
      },
    };

    this._swReg.showNotification(title, options)
      .catch((e) => console.warn('[FCM] showNotification failed:', e));
  },

  /**
   * Remove the FCM token from Firebase and the backend (call on logout).
   * Also resets the init guard so re-login gets a fresh token.
   */
  async removeToken() {
    try {
      if (_isIOS()) {
        // iOS uses VAPID — unsubscribe from push manager
        await pushNotificationService.unsubscribe();
      } else {
        const messaging = await getFirebaseMessaging();
        if (messaging) {
          await deleteToken(messaging);
        }
        await api.delete('/fcm/token');
        localStorage.removeItem(FCM_TOKEN_KEY);
      }
      // Reset so the next login triggers a fresh init
      this._initPromise = null;
      this._swReg = null;
      console.info('[FCM] Token removed.');
    } catch (e) {
      console.warn('[FCM] Token removal failed:', e.message);
    }
  },
};

export default fcmService;
